import { cn } from '@lyearn/style';
import { PostFragment } from '@/features/messages/graphql/fragments/generated/Post';
import { getPreviewElements } from '@/modules/corporate/modules/channel/views/ChannelDetails/utils/getPreviewElements';
import { CourseContentType, MentionEntityTypeEnum, UserType } from '@/types/schema';

import { pinnedHoverStyles, pinnedStyles } from './styles';
import { MessageItemConfig } from './types';

const DEFAULT_MESSAGE_CONFIG: MessageItemConfig = {
  MessageItem: {
    classes: {
      hover: 'hover:bg-bg-secondary',
    },
  },
  CreatedBy: {
    color: 'text-text-primary',
  },
  Body: {
    color: 'text-text-paragraph',
  },
};

const PINNED_MESSAGE_CONFIG: MessageItemConfig = {
  ...DEFAULT_MESSAGE_CONFIG,
  MessageItem: {
    classes: {
      root: cn(pinnedStyles, 'message-pinned'),
      hover: pinnedHoverStyles,
    },
  },
};

const HIDDEN_MESSAGE_CONFIG: MessageItemConfig = {
  ...DEFAULT_MESSAGE_CONFIG,
  CreatedBy: {
    color: 'text-text-placeholder',
  },
  Body: {
    color: 'text-text-placeholder',
  },
  Profile: {
    className: 'grayscale, opacity-50',
  },
};

export const getMessageItemConfig = (message: PostFragment) => {
  if (message.hiddenBy) {
    return HIDDEN_MESSAGE_CONFIG;
  }

  if (message.pinnedBy) {
    return PINNED_MESSAGE_CONFIG;
  }

  return DEFAULT_MESSAGE_CONFIG;
};

export const adaptMessage = (htmlString: string, mentions: PostFragment['mentions']) => {
  const mentionedContentMap = new Map<string, string>();
  const mentionedEventstMap = new Map<string, string>();
  const mentionedUsersMap = new Map<string, string>();
  mentions?.forEach((mention) => {
    const { entityType, contentMention, eventMention, entityMention } = mention;
    if (entityType === MentionEntityTypeEnum.Content && contentMention) {
      mentionedContentMap.set(
        (contentMention as CourseContentType).id,
        (contentMention as CourseContentType).name,
      );
    }
    if (entityType === MentionEntityTypeEnum.Event && eventMention) {
      mentionedEventstMap.set(eventMention.id, eventMention.name);
    }
    if (entityType === MentionEntityTypeEnum.User && entityMention) {
      mentionedUsersMap.set((entityMention as UserType).id, (entityMention as UserType).name || '');
    }
  });
  const htmlNode = document.createElement('div');
  htmlNode.innerHTML = htmlString;
  const { contentElements, eventElements, userElements } = getPreviewElements(htmlNode);

  if (
    (!contentElements.length || !mentionedContentMap.size) &&
    (!eventElements.length || !mentionedEventstMap.size) &&
    (!userElements.length || !mentionedUsersMap.size)
  ) {
    return htmlString;
  }

  const updateMentionNodeText = (element: Element, mentionEntityMap: Map<string, string>) => {
    const mentionId = (element as HTMLElement).dataset.mentionId;
    if (!mentionId) {
      return;
    }
    const resolvedMentionName = mentionEntityMap.get(mentionId);
    if (resolvedMentionName) {
      element.textContent = `@${resolvedMentionName}`;
    }
  };

  contentElements?.forEach((contentEl) => updateMentionNodeText(contentEl, mentionedContentMap));
  eventElements?.forEach((eventEl) => updateMentionNodeText(eventEl, mentionedEventstMap));
  userElements?.forEach((userEl) => updateMentionNodeText(userEl, mentionedUsersMap));

  return htmlNode.innerHTML;
};

export function extractH3Text(htmlString?: string) {
  if (!htmlString) {
    return null;
  }
  const match = htmlString.match(/<h3[^>]*>(.*?)<\/h3>/i);
  return match ? match[1].trim() : null;
}

export function extractKnowledgeDocTitle(htmlString?: string) {
  if (!htmlString) return null;

  const normalized = htmlString.replace(/\n/g, ' ');
  const match = normalized.match(
    /<h2[^>]*>.*?<\/h2>.*?<ul[^>]*>.*?<li[^>]*>.*?<p[^>]*>(.*?)<\/p>/i,
  );

  return match ? match[1].trim() : null;
}

export function removeFirstH2AndFollowingUl(htmlString?: string) {
  if (!htmlString) return '';

  const normalized = htmlString.replace(/\n/g, ' ');

  // Match <h2>...</h2> followed by <ul>...</ul>
  const cleaned = normalized.replace(/<h2[^>]*>.*?<\/h2>\s*<ul[^>]*>.*?<\/ul>/i, '');

  return cleaned.trim();
}

export function removeLeadingH3(htmlString?: string) {
  if (!htmlString) {
    return null;
  }
  return htmlString.replace(/^<h3[^>]*>.*?<\/h3>/i, '').trim();
}

export function extractPlaintextFromHTML(htmlString?: string | null) {
  if (!htmlString) {
    return null;
  }
  const htmlNode = document.createElement('div');
  htmlNode.innerHTML = htmlString;
  return htmlNode.textContent;
}

/**
 * Normalises a rich Coach answer into note-safe semantic HTML before it is sent
 * to `createNote`. `createNote` converts the HTML into editor blocks server-side
 * (`lyearn-resource-service` → `@lyearn/html-to-prosemirror`), and that converter
 * is a *reduced* one with hard limits we have to design around:
 *   - It has NO table and NO blockquote node, so `<table>`/`<blockquote>` (and
 *     all their text) are silently DROPPED — this is why tables never showed up
 *     in notes.
 *   - Its callout node only matches a `<callout>` element, never
 *     `<div class="callout">`; and a pre-pass turns every `<div>` into `<p>`,
 *     which would illegally nest the callout's inner `<p>`.
 *   - Table/atom nodes reject raw inline text and a `language-*` class on a
 *     `<code>` makes its CodeBlock node throw.
 * So we reduce the answer to the supported subset (paragraph / heading / list /
 * code_block / inline marks), converting the unsupported rich blocks into
 * faithful, readable equivalents instead of leaving markup the converter drops.
 */
export function sanitizeHtmlForNote(htmlString?: string | null): string {
  if (!htmlString) {
    return '';
  }
  if (typeof document === 'undefined') {
    return htmlString;
  }

  const root = document.createElement('div');
  root.innerHTML = htmlString;

  const el = (tag: string, text?: string) => {
    const node = document.createElement(tag);
    if (text != null) {
      node.textContent = text;
    }
    return node;
  };

  // KaTeX → its source LaTeX (or rendered text) so math survives as readable text.
  root.querySelectorAll('.katex-display, .katex').forEach((node) => {
    const tex = node.querySelector('annotation[encoding="application/x-tex"]')?.textContent;
    const replacement = (tex || node.textContent || '').trim();
    node.replaceWith(document.createTextNode(replacement));
  });

  // Mention wrappers → just the visible label/text.
  root.querySelectorAll('.ProseMirror__dom').forEach((node) => {
    const anchor = node.querySelector('a');
    node.replaceWith(document.createTextNode((anchor || node).textContent || ''));
  });

  // Tables → readable "Header: value" paragraphs. The note converter has no
  // table node and would otherwise drop the table and all of its content.
  root.querySelectorAll('table').forEach((table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (!rows.length) {
      table.remove();
      return;
    }
    const headers = Array.from(rows[0].querySelectorAll('th')).map((c) =>
      (c.textContent || '').trim(),
    );
    const hasHeader = headers.length > 0;
    const fragment = document.createDocumentFragment();

    rows.forEach((row, rowIndex) => {
      if (hasHeader && rowIndex === 0) {
        return;
      }
      const cells = Array.from(row.querySelectorAll('td,th'));
      if (!cells.length) {
        return;
      }
      const paragraph = el('p');
      cells.forEach((cell, cellIndex) => {
        if (cellIndex > 0) {
          paragraph.appendChild(document.createTextNode('  •  '));
        }
        const label = hasHeader ? headers[cellIndex] : '';
        if (label) {
          paragraph.appendChild(el('strong', `${label}: `));
        }
        paragraph.appendChild(document.createTextNode((cell.textContent || '').trim()));
      });
      fragment.appendChild(paragraph);
    });

    if (hasHeader && rows.length === 1) {
      fragment.appendChild(el('p', headers.join('  •  ')));
    }
    table.replaceWith(fragment);
  });

  // Callouts → a labelled paragraph group. The converter cannot build a callout
  // block from `<div class="callout">`, so we unwrap and prefix a bold label so
  // the "Tip/Note/Warning" intent still reads naturally in the note.
  root.querySelectorAll('.callout').forEach((node) => {
    const type = (node.getAttribute('data-callout') || '').trim();
    const label = type ? `${type.charAt(0).toUpperCase()}${type.slice(1)}: ` : '';
    const fragment = document.createDocumentFragment();
    let labelled = false;
    Array.from(node.childNodes).forEach((child) => {
      if (label && !labelled && child.nodeType === 1 && (child as Element).tagName === 'P') {
        (child as Element).insertBefore(el('strong', label), (child as Element).firstChild);
        labelled = true;
      }
      fragment.appendChild(child);
    });
    if (label && !labelled) {
      const paragraph = el('p');
      paragraph.appendChild(el('strong', label));
      fragment.insertBefore(paragraph, fragment.firstChild);
    }
    node.replaceWith(fragment);
  });

  // Blockquotes → their inner blocks, lifted up (the converter has no
  // blockquote node). markdown-it already wraps quote content in `<p>`/list
  // nodes, so unwrapping keeps valid block structure and avoids nesting a
  // block `<p>` inside another `<p>`.
  root.querySelectorAll('blockquote').forEach((node) => {
    const fragment = document.createDocumentFragment();
    if (node.childElementCount === 0) {
      // Bare text quote (rare) — wrap once so it survives as a paragraph.
      fragment.appendChild(el('p', (node.textContent || '').trim()));
    } else {
      while (node.firstChild) {
        fragment.appendChild(node.firstChild);
      }
    }
    node.replaceWith(fragment);
  });

  // Unwrap any remaining wrapper <div>s so nothing relies on a div surviving
  // (the server pre-pass rewrites every <div> to <p>, which breaks nesting).
  let guard = 0;
  let div = root.querySelector('div');
  while (div && guard < 5000) {
    const fragment = document.createDocumentFragment();
    while (div.firstChild) {
      fragment.appendChild(div.firstChild);
    }
    div.replaceWith(fragment);
    div = root.querySelector('div');
    guard += 1;
  }

  // Drop presentational attributes. Also removes the `language-*` class that
  // makes the converter's code-block node throw, and keeps inline <code> as a
  // plain code mark.
  root.querySelectorAll('*').forEach((node) => {
    node.removeAttribute('class');
    node.removeAttribute('style');
    node.removeAttribute('data-callout');
    node.removeAttribute('contenteditable');
  });

  return root.innerHTML;
}
